import React from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  Globe,
  CreditCard,
  ShieldCheck,
  Layers,
  ArrowRight,
  Lock,
  Users,
  CheckCircle2,
  X,
  Trophy,
  GraduationCap,
  Building2,
  MousePointerClick,
  Calculator,
  FileText,
  CheckSquare,
  DollarSign,
  FileSpreadsheet
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Button,
  Badge as BaseBadge,
  Modal,
  Input
} from '../components/ui';
import { cn } from '../lib/utils';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { useCreateNode, checkNodeNameAvailable, useCheckNodeName } from '../hooks/useNode';
import { PublicFooter } from '../components/shell/PublicFooter';
import { PublicHeader } from '../components/shell/PublicHeader';
import { HeroDashboardMock } from '../components/shell/HeroDashboardMock';


import { SEO } from '../components/SEO';
import { DEFAULT_ENABLED_MODULES } from '../constants';
import { toast } from 'react-hot-toast';

const Badge: React.FC<{
  children: React.ReactNode;
  variant?: 'blue' | 'amber' | 'purple' | 'pink' | 'cyan' | 'lime' | 'outline' | 'secondary';
  className?: string
}> = ({ children, variant = 'blue', className }) => {
  const variants = {
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700',
    pink: 'bg-pink-500/10 text-pink-500',
    cyan: 'bg-cyan-500/10 text-cyan-500',
    lime: 'bg-lime-500/10 text-lime-500',
    outline: 'border border-gray-200 text-gray-500',
    secondary: 'bg-gray-100 text-gray-700 dark:bg-slate-800'
  };
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', variants[variant], className)}>
      {children}
    </span>
  );
};

// --- FAQ Accordion Item ---
const FaqItem: React.FC<{ q: string; a: string; defaultOpen?: boolean; index: number }> = ({ q, a, defaultOpen = false, index }) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="border-[4px] border-black bg-white shadow-[8px_8px_0px_#000000] overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between text-left group"
      >
        <h3 className="text-xl font-black uppercase pr-4">{q}</h3>
        <div className={cn(
          "w-10 h-10 border-[3px] border-black flex items-center justify-center shrink-0 font-black text-xl transition-colors",
          isOpen ? "bg-black text-white" : "bg-white text-black group-hover:bg-[#0044FF] group-hover:text-white group-hover:border-[#0044FF]"
        )}>
          {isOpen ? '−' : '+'}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="px-6 pb-6">
              <p className="font-bold text-gray-700">{a}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// --- Setup Wizard Subcomponent ---

const SetupWizard: React.FC<{ isOpen: boolean; onClose: () => void; initialName?: string }> = ({ isOpen, onClose, initialName = '' }) => {
  const { isSignedIn, isBackendSynced, isSyncing } = useCurrentUser();
  const createNode = useCreateNode();
  const [step, setStep] = React.useState(1);
  const [selections, setSelections] = React.useState({
    name: initialName,
    structure: 'network', // network | club | board
    operations: 'finance', // finance vs comms
    reach: 'public', // private vs public
    visibility: 'PUBLIC' // PUBLIC, PROTECTED, PRIVATE
  });

  React.useEffect(() => {
    if (isOpen && initialName) {
      setSelections(prev => ({ ...prev, name: initialName }));
    }
  }, [isOpen, initialName]);

  const currentSlug = selections.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const { data: isNameAvailable, isLoading: isCheckingName } = useCheckNodeName(selections.name);

  const handleNext = (key: string, value: string) => {
    setSelections(prev => ({ ...prev, [key]: value }));
    setStep(s => s + 1);
  };

  const reset = () => {
    setStep(1);
    setSelections({ name: initialName || '', structure: 'network', operations: 'finance', reach: 'public', visibility: 'PUBLIC' });
  };

  const closeHandler = () => {
    onClose();
    setTimeout(reset, 500);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeHandler}
      title={step === 4 ? "Claim & Launch Organization" : `Step ${step} of 3`}
      className="md:max-w-2xl"
    >
      <div className="space-y-6">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="space-y-4"
            >
              <h3 className="text-xl font-black font-display uppercase tracking-tight text-navy-900 dark:text-white">
                INITIALIZE YOUR WORKSPACE. What is your org called?
              </h3>
              <p className="text-sm text-gray-500 font-sans">
                Watch your live public web address generate and check availability in real time.
              </p>
              
              <Input
                placeholder="e.g. Memphis Track Club"
                value={selections.name}
                onChange={(e) => setSelections({ ...selections, name: e.target.value })}
                autoFocus
                className="font-bold text-lg"
              />

              {/* Real-Time Live Slug & Availability Preview */}
              <div className="bg-gray-50 dark:bg-zinc-900 border-[3px] border-black dark:border-white p-4 font-mono shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center justify-between">
                  <span>Public Web Address</span>
                  <span className="text-[10px] bg-black text-white dark:bg-white dark:text-black px-1.5 py-0.5 font-bold uppercase tracking-widest">Live Check</span>
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-bold text-base text-black dark:text-white break-all">
                    orgsets.com/<span className="text-[#0044FF] dark:text-[#FFD600] font-black">{currentSlug || 'your-org-name'}</span>
                  </span>
                  {currentSlug.length >= 2 && (
                    isCheckingName ? (
                      <span className="text-xs text-gray-500 font-bold animate-pulse">Checking...</span>
                    ) : isNameAvailable ? (
                      <span className="text-xs font-black text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/80 border border-emerald-500 px-2.5 py-1 uppercase tracking-wider flex items-center gap-1.5">
                        <CheckCircle2 size={13} className="text-emerald-600" /> Available!
                      </span>
                    ) : (
                      <span className="text-xs font-black text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-950/80 border border-rose-500 px-2.5 py-1 uppercase tracking-wider">
                        Taken — Choose another
                      </span>
                    )
                  )}
                </div>
              </div>

              <div className="pt-4">
                <Button
                  disabled={!selections.name.trim() || currentSlug.length < 2 || isNameAvailable === false}
                  size="lg"
                  onClick={() => setStep(2)}
                  className="font-black uppercase tracking-wider h-14 w-full bg-black text-white dark:bg-white dark:text-black hover:bg-[#0044FF] hover:text-white transition-colors rounded-none border-[3px] border-black dark:border-white shadow-[4px_4px_0px_#0044FF] dark:shadow-[4px_4px_0px_#FFD600]"
                >
                  Continue to Org Type →
                </Button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="space-y-4"
            >
              <h3 className="text-xl font-black font-display uppercase tracking-tight text-navy-900 dark:text-white">
                Select organization type:
              </h3>
              <p className="text-sm text-gray-500 font-sans">
                Choose the architecture that best models your community structure.
              </p>
              
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => handleNext('structure', 'network')}
                  className={cn(
                    "flex items-center justify-between p-5 rounded-none border-[3px] text-left transition-all group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]",
                    selections.structure === 'network' ? "border-[#0044FF] bg-blue-50/50 dark:bg-blue-950/30" : "border-black dark:border-white hover:border-[#0044FF]"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-none bg-brand-primary/10 border-2 border-black dark:border-white flex items-center justify-center text-brand-primary shrink-0">
                      <Layers size={24} />
                    </div>
                    <div>
                      <div className="font-black uppercase tracking-tight text-gray-900 dark:text-white text-base">Multi-Chapter Network</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">Hierarchical parent & child chapters with aggregated rosters and tiered governance.</div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 group-hover:text-brand-primary" />
                </button>

                <button
                  type="button"
                  onClick={() => handleNext('structure', 'club')}
                  className={cn(
                    "flex items-center justify-between p-5 rounded-none border-[3px] text-left transition-all group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]",
                    selections.structure === 'club' ? "border-[#0044FF] bg-blue-50/50 dark:bg-blue-950/30" : "border-black dark:border-white hover:border-[#0044FF]"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-none bg-indigo-50 border-2 border-black dark:border-white flex items-center justify-center text-indigo-600 shrink-0">
                      <Users size={24} />
                    </div>
                    <div>
                      <div className="font-black uppercase tracking-tight text-gray-900 dark:text-white text-base">Local Club / League</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">Independent athletic league, running crew, booster club, or local sports group.</div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 group-hover:text-brand-primary" />
                </button>

                <button
                  type="button"
                  onClick={() => handleNext('structure', 'board')}
                  className={cn(
                    "flex items-center justify-between p-5 rounded-none border-[3px] text-left transition-all group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]",
                    selections.structure === 'board' ? "border-[#0044FF] bg-blue-50/50 dark:bg-blue-950/30" : "border-black dark:border-white hover:border-[#0044FF]"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-none bg-emerald-50 border-2 border-black dark:border-white flex items-center justify-center text-emerald-600 shrink-0">
                      <Building2 size={24} />
                    </div>
                    <div>
                      <div className="font-black uppercase tracking-tight text-gray-900 dark:text-white text-base">HOA / Nonprofit Board</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">Homeowners association, charitable foundation, or governing board with digital compliance vault.</div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 group-hover:text-brand-primary" />
                </button>
              </div>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-xs font-mono font-bold uppercase tracking-wider text-gray-500 hover:text-black dark:hover:text-white flex items-center gap-1 mt-2"
              >
                <ChevronLeft size={16} /> Change Name
              </button>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="space-y-4"
            >
              <h3 className="text-xl font-black font-display uppercase tracking-tight text-navy-900 dark:text-white">
                Define visibility & access protocols:
              </h3>
              <p className="text-sm text-gray-500 font-sans">
                Control who can discover and join your organization.
              </p>
              
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => handleNext('visibility', 'PUBLIC')}
                  className={cn(
                    "flex items-center justify-between p-5 rounded-none border-[3px] text-left transition-all group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]",
                    selections.visibility === 'PUBLIC' ? "border-[#0044FF] bg-blue-50/50 dark:bg-blue-950/30" : "border-black dark:border-white hover:border-[#0044FF]"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-none bg-blue-50 border-2 border-black dark:border-white flex items-center justify-center text-blue-600 shrink-0">
                      <Globe size={24} />
                    </div>
                    <div>
                      <div className="font-black uppercase tracking-tight text-gray-900 dark:text-white text-base">Everyone! (Public)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">Open network. Publicly discoverable directory and events.</div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 group-hover:text-brand-primary" />
                </button>

                <button
                  type="button"
                  onClick={() => handleNext('visibility', 'PROTECTED')}
                  className={cn(
                    "flex items-center justify-between p-5 rounded-none border-[3px] text-left transition-all group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]",
                    selections.visibility === 'PROTECTED' ? "border-[#0044FF] bg-blue-50/50 dark:bg-blue-950/30" : "border-black dark:border-white hover:border-[#0044FF]"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-none bg-amber-50 border-2 border-black dark:border-white flex items-center justify-center text-amber-600 shrink-0">
                      <ShieldCheck size={24} />
                    </div>
                    <div>
                      <div className="font-black uppercase tracking-tight text-gray-900 dark:text-white text-base">New Friends (Protected)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">Protected network. Member application & admin approval required.</div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 group-hover:text-brand-primary" />
                </button>

                <button
                  type="button"
                  onClick={() => handleNext('visibility', 'PRIVATE')}
                  className={cn(
                    "flex items-center justify-between p-5 rounded-none border-[3px] text-left transition-all group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]",
                    selections.visibility === 'PRIVATE' ? "border-[#0044FF] bg-blue-50/50 dark:bg-blue-950/30" : "border-black dark:border-white hover:border-[#0044FF]"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-none bg-rose-50 border-2 border-black dark:border-white flex items-center justify-center text-rose-600 shrink-0">
                      <Lock size={24} />
                    </div>
                    <div>
                      <div className="font-black uppercase tracking-tight text-gray-900 dark:text-white text-base">Inner Circle (Private)</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">Private network. Strict invitation-only access.</div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 group-hover:text-brand-primary" />
                </button>
              </div>

              <button
                type="button"
                onClick={() => setStep(2)}
                className="text-xs font-mono font-bold uppercase tracking-wider text-gray-500 hover:text-black dark:hover:text-white flex items-center gap-1 mt-2"
              >
                <ChevronLeft size={16} /> Back to Org Type
              </button>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-2 space-y-5"
            >
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 border-[3px] border-black dark:border-white rounded-none flex items-center justify-center mx-auto shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
                <CheckCircle2 size={36} />
              </div>

              <div className="space-y-1.5">
                <h3 className="text-2xl font-black text-navy-900 dark:text-white font-display uppercase tracking-tight">
                  Org Ready for Launch
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm font-sans max-w-md mx-auto">
                  Create your admin account to claim <span className="font-mono font-bold text-[#0044FF] dark:text-[#FFD600]">orgsets.com/{currentSlug}</span>.
                </p>
              </div>

              {/* Bauhaus Summary Card */}
              <div className="bg-white dark:bg-black p-6 text-left border-[4px] border-black dark:border-white shadow-[6px_6px_0px_#0044FF] dark:shadow-[6px_6px_0px_#FFD600]">
                <div className="flex items-center justify-between mb-4 border-b-2 border-black/10 dark:border-white/10 pb-3 flex-wrap gap-2">
                  <span className="font-mono text-xs font-black uppercase px-2.5 py-1 bg-black text-white dark:bg-white dark:text-black tracking-wider">
                    {selections.structure === 'network' ? 'MULTI-CHAPTER NETWORK' : selections.structure === 'board' ? 'HOA / NONPROFIT BOARD' : 'LOCAL CLUB / LEAGUE'}
                  </span>
                  <span className="text-xs font-mono font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest bg-emerald-50 dark:bg-emerald-950/60 px-2 py-0.5 border border-emerald-500/40">
                    $0 / Free to start
                  </span>
                </div>

                <div className="space-y-3 font-mono text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 font-bold uppercase">Reserved URL:</span>
                    <span className="font-bold text-black dark:text-white text-sm">orgsets.com/{currentSlug}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 font-bold uppercase">Access Protocol:</span>
                    <span className="font-bold text-black dark:text-white">{selections.visibility} Profile</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 font-bold uppercase">Core Modules:</span>
                    <span className="font-bold text-black dark:text-white">Roster • Dues & Splits • Vault • Pulse</span>
                  </div>
                </div>
              </div>

              {/* Primary Owner Security Guarantee Note */}
              <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700/60 p-3 text-left text-xs font-mono">
                <span className="font-black uppercase tracking-wider text-amber-900 dark:text-amber-200 block mb-0.5">
                  🔒 Primary Owner Guarantee
                </span>
                <span className="text-amber-800 dark:text-amber-300/90 font-medium">
                  Creating your account immediately registers you as the primary Owner and active Administrator. Organizations are never provisioned without an authenticated owner.
                </span>
              </div>

              <div className="pt-2 flex flex-col gap-3">
                <Button
                  size="lg"
                  className="h-14 font-black uppercase tracking-wider text-base bg-black text-white dark:bg-white dark:text-black hover:bg-[#0044FF] hover:text-white transition-colors rounded-none border-[3px] border-black dark:border-white shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff]"
                  isLoading={createNode.isPending || isSyncing}
                  disabled={isSignedIn && isSyncing}
                  onClick={async () => {
                    if (!selections.name.trim()) {
                      toast.error("Please enter an organization name.");
                      return;
                    }

                    const available = await checkNodeNameAvailable(selections.name);
                    if (!available) {
                      toast.error(`Organization name "${selections.name}" is already taken. Please choose another name.`);
                      setStep(1);
                      return;
                    }

                    const slug = selections.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                    const modules = [...DEFAULT_ENABLED_MODULES];

                    const data = {
                      name: selections.name,
                      slug,
                      template: selections.structure === 'network' ? 'NETWORK' : 'CLUB',
                      visibility: selections.visibility,
                      tier: selections.structure === 'network' ? 'INSTITUTIONAL' : 'LOCAL',
                      modules
                    };

                    if (isSignedIn) {
                      if (!isBackendSynced) {
                        toast.error("Account verification in progress. Please complete account sync before creating an organization.");
                        return;
                      }
                      try {
                        await createNode.mutateAsync(data);
                        window.location.href = `/dashboard`;
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to create org. Please try again.");
                      }
                    } else {
                      localStorage.setItem('pending_hub_creation', JSON.stringify(data));
                      window.location.href = '/sign-up?redirect_url=/dashboard';
                    }
                  }}
                >
                  {!isSignedIn 
                    ? `Create Admin Account & Claim orgsets.com/${currentSlug} →` 
                    : isSyncing 
                      ? 'Verifying User Account...' 
                      : 'Generate My Workspace'}
                </Button>
                
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-xs font-mono font-bold uppercase tracking-wider text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Wait, let me change something
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  );
};

const TONE_OPTIONS = [
  { id: 'professional', label: 'Supportive & Professional', icon: '💼' },
  { id: 'collaborative', label: 'Community Friendly', icon: '🤝' },
  { id: 'minimalist', label: 'Clean & Simple', icon: '✨' },
  { id: 'high-octane', label: 'High-Octane (Bold)', icon: '⚡' }
] as const;

type ToneId = typeof TONE_OPTIONS[number]['id'];

const TONE_COPIES: Record<ToneId, {
  badge: string;
  titleLine1: string;
  titleLine2: string;
  p: string;
  cta: string;
  secondaryCta: string;
}> = {
  professional: {
    badge: 'Organize Smartly',
    titleLine1: 'Lead your group with',
    titleLine2: 'quiet confidence.',
    p: 'Easily bring calendar events, secure files, and group finances together under a single professional dashboard. Designed specifically for community orgs, non-profits, and leagues.',
    cta: 'CREATE AN ORG',
    secondaryCta: 'EXPLORE FEATURES'
  },
  collaborative: {
    badge: 'Stronger Together',
    titleLine1: 'The friendly space for',
    titleLine2: 'your group operations.',
    p: 'Keep members connected, schedule joint meetings, and manage dues transparency with simple, collaborative tools built specifically with local groups in mind.',
    cta: 'GET STARTED FOR FREE',
    secondaryCta: 'SEE HOW IT WORKS'
  },
  minimalist: {
    badge: 'Pure Utility',
    titleLine1: 'Your entire organization',
    titleLine2: 'in one clear, simple home.',
    p: 'A secure, ad-free platform for your files, schedules, and announcements. Free to start, easy to manage, and built for privacy.',
    cta: 'Start a free org',
    secondaryCta: 'See how it works'
  },
  'high-octane': {
    badge: 'Pure Utility',
    titleLine1: 'BUILT FOR LEADERS.',
    titleLine2: 'Run your network faster.',
    p: 'Eliminate administrative friction. Deploy a professional command center for your entire group in under 60 seconds.',
    cta: 'Initialize your org',
    secondaryCta: 'View architecture'
  }
};

const LANDING_FAQS = [
  { 
    q: "Is it really free to start? What's the catch?", 
    a: "No catch, no ads, and we never sell member data. OrgSets is free to start for local clubs (up to 50 members) because our business model is simple: we earn a flat $0.50 platform fee per dues payment collected through Stripe, and offer paid plans as your roster grows and needs multi-tier parent networks, SMS blasts, or expanded storage." 
  },
  { 
    q: "How hard is migration?", 
    a: "Minutes. Bulk-import your roster via CSV. For large orgs, our team will migrate you for free." 
  },
  { 
    q: "Who owns the data?", 
    a: "You do. We never sell member data or hold records hostage. Export your full roster, ledger history, and documents at any time." 
  }
];

export const LandingPage = () => {
  const { node, brand, colorMode, activeBrandId } = useTenant();
  const { isSignedIn } = useCurrentUser();
  const [isWizardOpen, setIsWizardOpen] = React.useState(false);
  const [heroOrgName, setHeroOrgName] = React.useState('');

  const heroSlug = heroOrgName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const { data: isHeroAvailable, isLoading: isCheckingHero } = useCheckNodeName(heroOrgName);

  const handleClaimSubmit = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (heroOrgName.trim()) {
      const slug = heroSlug || heroOrgName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const data = {
        name: heroOrgName.trim(),
        slug,
        template: 'CLUB',
        visibility: 'PUBLIC',
        tier: 'LOCAL',
        modules: [...DEFAULT_ENABLED_MODULES]
      };
      try {
        localStorage.setItem('pending_hub_creation', JSON.stringify(data));
      } catch {
        /* ignore */
      }
      if (isSignedIn) {
        setIsWizardOpen(true);
      } else {
        window.location.href = `/sign-up?slug=${encodeURIComponent(slug)}&name=${encodeURIComponent(heroOrgName.trim())}&redirect_url=/dashboard`;
      }
    } else {
      if (isSignedIn) {
        setIsWizardOpen(true);
      } else {
        window.location.href = '/sign-up?redirect_url=/dashboard';
      }
    }
  };

  const brandName = brand.name;
  const copy = node?.config?.marketingCopy;
  const theme = activeBrandId || 'minimal';

  const isDark = colorMode === 'dark';

  const s = {
    wrapper: 'bg-white text-black dark:bg-black dark:text-white transition-colors duration-300',
    header: 'bg-white dark:bg-black border-b-[4px] border-black dark:border-white',
    navText: 'text-black dark:text-white hover:text-brand-primary',
    logoText: 'text-black dark:text-white font-black',
    hero: 'bg-white dark:bg-black text-black dark:text-white relative overflow-hidden',
    heroBg: null,
    heroH1: 'text-black dark:text-white font-black tracking-tighter',
    heroP: 'text-black dark:text-white font-bold',
    secondaryBtn: 'bg-white dark:bg-black border-[4px] border-black dark:border-white text-black dark:text-white hover:bg-brand-primary hover:text-white transition-colors rounded-none shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] font-black',
    featuresSection: 'bg-white text-black dark:bg-black dark:text-white border-t-[8px] border-black dark:border-white',
    featuresH2: 'tracking-tighter text-black dark:text-white',
    featuresP: 'text-black dark:text-white font-bold',
    bentoCard: 'bg-white dark:bg-black text-black dark:text-white border-[4px] border-black dark:border-white rounded-none shadow-[8px_8px_0px_#0044FF] hover:shadow-[12px_12px_0px_#FFD600] hover:-translate-x-1 hover:-translate-y-1 transition-all',
    pulseIcon: 'bg-brand-primary/10 text-brand-primary dark:bg-brand-primary/20',
    bentoTitle: 'uppercase tracking-tighter text-black dark:text-white font-black',
    bentoText: 'font-bold text-black dark:text-white opacity-80',
    bentoSample: 'bg-gray-50 dark:bg-slate-900 border border-gray-100 dark:border-slate-800',
    heroButtonClass: 'bg-white dark:bg-black text-black dark:text-white border-[4px] border-black dark:border-white hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black font-black px-10 py-5 shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] transition-all rounded-none hover:translate-y-1 hover:translate-x-1 hover:shadow-[4px_4px_0px_#000] dark:hover:shadow-[4px_4px_0px_#fff]',
    browseBg: 'bg-[#0044FF] text-white border-t-[8px] border-b-[8px] border-black dark:border-white',
    ledgerCard: 'bg-white dark:bg-slate-950 border border-gray-100 dark:border-slate-800 shadow-sm hover:border-brand-primary/30 transition-all text-gray-900 dark:text-white rounded-2xl'
  };


  const landingSchema = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "OrgSets",
      "url": "https://orgsets.com",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://orgsets.com/showcase?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "OrgSets",
      "url": "https://orgsets.com",
      "logo": "https://orgsets.com/favicon.svg",
      "sameAs": [
        "https://twitter.com/orgsets",
        "https://github.com/richardtreharne-tech/OrgSets-UI"
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "OrgSets",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web, All modern browsers",
      "url": "https://orgsets.com",
      "description": "The operating system for multi-chapter networks, leagues, and non-profits. Unified rosters, automated chapter dues splitting via Stripe, instant SMS/email alerts, and role-based document vault.",
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "USD",
        "lowPrice": "0",
        "highPrice": "199",
        "offerCount": "4",
        "offers": [
          {
            "@type": "Offer",
            "name": "Free Starter Hub",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free for local organizations up to 50 members with zero subscription fees. Flat $0.50 platform fee per dues payment collected via Stripe."
          },
          {
            "@type": "Offer",
            "name": "Foundation",
            "price": "29",
            "priceCurrency": "USD",
            "description": "Up to 250 members, broadcast SMS and email alerts, automated chapter dues splitting, and custom branding."
          },
          {
            "@type": "Offer",
            "name": "Momentum",
            "price": "79",
            "priceCurrency": "USD",
            "description": "Up to 1,000 members, multi-tier parent-chapter rollups, advanced financial ledger exports, and custom domains."
          },
          {
            "@type": "Offer",
            "name": "Institutional",
            "price": "199",
            "priceCurrency": "USD",
            "description": "Unlimited members and chapters, full hierarchy governance, audit logging, and dedicated SLA."
          }
        ]
      },
      "featureList": [
        "Multi-chapter network command center",
        "Automated Stripe dues splitting and financial ledger",
        "Broadcast emergency SMS and email alerts",
        "Role-based secure document vault",
        "Live member and officer directory",
        "100+ free zero-login operational tools and calculators"
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": LANDING_FAQS.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    }
  ];

  return (
    <div className="min-h-screen font-sans selection:bg-brand-primary/20 bg-white text-black dark:bg-black dark:text-white transition-colors duration-300">
      <SEO
        title="OrgSets | The Operating System for Multi-Chapter Networks & Leagues"
        description="OrgSets is the multi-chapter membership management software and chapter dues collection platform for networks, leagues, and boards. Automated national to local dues splitting, instant SMS alerts, and secure bylaws vault."
        url="https://orgsets.com"
        schema={landingSchema}
      />
      <PublicHeader />

      <main>
        {/* ═══════════════════════════════════════════════════════════════
            SECTION 1: HERO — Simplified, single CTA focus
        ═══════════════════════════════════════════════════════════════ */}
          <section className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white min-h-[80vh] bg-white dark:bg-black text-black dark:text-white relative overflow-hidden">
            <div className="px-4 py-8 pt-28 md:p-16 md:pt-32 lg:p-12 lg:pt-32 xl:p-24 xl:pt-40 flex flex-col justify-center border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white">
              <h1 className="mb-6 md:mb-8">
                <span className="block text-xs sm:text-sm md:text-base font-mono font-black tracking-widest text-[#0044FF] dark:text-[#FFD600] uppercase mb-4">
                  Multi-Chapter Membership Management Software
                </span>
                <span className="text-5xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-7xl 2xl:text-[100px] font-black uppercase tracking-tighter leading-[0.85] block break-words">
                  <span className="block">{copy?.heroHeadline || 'RUN YOUR ENTIRE NETWORK—'}</span>
                  <span className="text-brand-primary block">{copy?.heroSubheadline || 'FROM NATIONAL HQ TO LOCAL CHAPTERS.'}</span>
                </span>
              </h1>
              <p className="text-xl md:text-2xl font-bold max-w-xl leading-snug mb-6 md:mb-8">
                {copy?.heroDescription || 'The all-in-one multi-chapter membership management software and club & association management software. Replace Mailchimp, Venmo, Google Drive, and group chats with one command center: unified rosters, automated national to local dues splitting, and instant SMS alerts.'}
              </p>

              {/* Step 1 Pre-Auth Claim Input on Landing Page Hero */}
              {(!node || node.slug === 'global' || node.id === 'platform-root') && (
                <div className="w-full max-w-xl mb-8">
                  <div className="flex flex-col sm:flex-row border-[4px] border-black dark:border-white shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] bg-white dark:bg-black p-1.5 gap-2">
                    <div className="flex-1 flex items-center px-3 py-2 bg-gray-50 dark:bg-zinc-900 border border-black/10 dark:border-white/10 font-mono text-sm">
                      <span className="text-gray-400 select-none font-bold mr-1 hidden sm:inline">orgsets.com/</span>
                      <input
                        type="text"
                        placeholder="your-org-name"
                        value={heroOrgName}
                        onChange={(e) => setHeroOrgName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleClaimSubmit();
                          }
                        }}
                        className="bg-transparent border-none outline-none font-bold text-black dark:text-white w-full uppercase text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleClaimSubmit}
                      className="bg-black text-white dark:bg-white dark:text-black font-black uppercase text-sm tracking-wider px-6 py-3.5 hover:bg-[#0044FF] hover:text-white transition-colors shrink-0 flex items-center justify-center gap-2"
                    >
                      <span>Claim URL</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                  {heroSlug ? (
                    <div className="mt-2 flex items-center gap-2 text-xs font-mono">
                      {isCheckingHero ? (
                        <span className="text-gray-400 font-bold">Checking availability...</span>
                      ) : isHeroAvailable ? (
                        <span className="text-emerald-700 dark:text-emerald-400 font-black flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-950/80 border border-emerald-500/40 px-2.5 py-1">
                          <CheckCircle2 size={13} className="text-emerald-500" /> orgsets.com/{heroSlug} is Available!
                        </span>
                      ) : (
                        <span className="text-rose-600 dark:text-rose-400 font-bold flex items-center gap-1 bg-rose-50 dark:bg-rose-950/80 border border-rose-500/40 px-2 py-0.5">
                          Name taken, try another
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1.5 text-xs text-gray-500 font-mono">
                      Type your org name to test URL availability in real time.
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  {node && node.slug !== 'global' && node.id !== 'platform-root' ? (
                    <Button size="lg" className="w-full sm:w-auto bg-black text-white dark:bg-white dark:text-black font-bold uppercase tracking-wider py-4 px-8 rounded-none border-[4px] border-transparent hover:border-black dark:hover:border-white hover:bg-brand-primary transition-colors h-auto text-lg shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff]" onClick={() => window.location.href = '/dashboard'}>
                      {node.visibility === 'PUBLIC' ? 'ACCESS ORG' : node.visibility === 'PROTECTED' ? 'APPLY TO JOIN' : 'LAUNCH DASHBOARD'}
                    </Button>
                  ) : (
                    <a
                      href={heroSlug ? `/sign-up?slug=${encodeURIComponent(heroSlug)}&name=${encodeURIComponent(heroOrgName.trim())}&redirect_url=/dashboard` : '/sign-up?redirect_url=/dashboard'}
                      onClick={handleClaimSubmit}
                      className="w-full sm:w-auto bg-black text-white dark:bg-white dark:text-black px-10 py-5 font-black uppercase tracking-widest text-lg hover:bg-brand-primary hover:text-white transition-colors flex items-center justify-center gap-4 border-none outline-none rounded-none shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff]"
                    >
                      {heroOrgName.trim() ? `CLAIM ORGSETS.COM/${heroSlug} →` : (copy?.heroCta || 'CLAIM YOUR FREE URL →')}
                    </a>
                  )}
                  <a 
                    href="#compare"
                    className="w-full sm:w-auto inline-flex items-center justify-center bg-white dark:bg-black border-[4px] border-black dark:border-white text-black dark:text-white hover:bg-brand-secondary hover:text-black transition-colors rounded-none py-4 px-8 h-auto text-lg font-bold uppercase tracking-wider font-mono shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff]"
                  >
                    Compare Platforms ↓
                  </a>
                </div>
                <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest opacity-60 mt-2">
                  <span className="flex items-center gap-1.5"><CheckCircle2 size={14} /> Free to start</span>
                  <span className="flex items-center gap-1.5"><CheckCircle2 size={14} /> No credit card</span>
                  <span className="flex items-center gap-1.5"><CheckCircle2 size={14} /> Setup in 60s</span>
                </div>
              </div>
            </div>
            
            {/* Graphical Hero Asset — Interactive Live Preview */}
            <div id="preview" className="flex p-4 md:p-8 lg:p-12 flex-col justify-center items-center relative overflow-hidden bg-[#0044FF]">
              <div className="w-full max-w-3xl bg-white dark:bg-black border-[4px] md:border-[8px] border-black dark:border-white shadow-[8px_8px_0px_#000000] md:shadow-[16px_16px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] md:dark:shadow-[16px_16px_0px_#ffffff] relative group">
                {/* HUD Header Strip */}
                <div className="bg-black text-white px-3 py-2 border-b-2 border-black dark:border-white flex items-center justify-between font-mono text-[10px] font-bold">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="uppercase tracking-wider">Live Platform Preview</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[#FFD600]">
                    <MousePointerClick size={12} className="animate-bounce" />
                    <span className="uppercase tracking-wider hidden sm:inline">Click tabs to test features</span>
                  </div>
                </div>

                {/* Interactive Dashboard Container */}
                <div className="h-[460px] sm:h-[480px] md:h-[500px] relative overflow-hidden bg-slate-950">
                  <HeroDashboardMock />
                </div>
              </div>
            </div>
          </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 2: TRUST STRIP — Enterprise Security & Infrastructure
        ═══════════════════════════════════════════════════════════════ */}
        <section className="bg-white dark:bg-black border-b-[8px] border-black dark:border-white py-8 text-black dark:text-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <p className="text-xs font-black uppercase tracking-[0.25em] opacity-40 shrink-0">
                Enterprise Infrastructure
              </p>
              <div className="flex items-center gap-8 md:gap-14 flex-wrap justify-center">
                {[
                  { name: 'Stripe', desc: 'PCI-Compliant Payments' },
                  { name: 'AWS', desc: '256-Bit Encrypted Cloud' },
                  { name: 'Twilio', desc: 'Instant SMS Delivery' }
                ].map((tech) => (
                  <div key={tech.name} className="flex flex-col items-center gap-1">
                    <span className="text-sm font-black uppercase tracking-widest">{tech.name}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest opacity-50">{tech.desc}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-black uppercase tracking-widest opacity-60">All Systems Online</span>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 3: HOW IT WORKS — Moved UP for faster conversion
        ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4 bg-gray-50 dark:bg-gray-900 border-b-[8px] border-black dark:border-white text-black dark:text-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <Badge variant="blue" className="mb-6 border-none px-6 py-2 rounded-none border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] bg-[#0044FF] text-white font-black cursor-pointer uppercase tracking-widest">
                Simple Setup
              </Badge>
              <h2 className="text-4xl md:text-6xl font-black font-display tracking-tight leading-[0.9]">
                Deploy your chapter command center <span className="text-[#0044FF] dark:text-[#FFD600]">in 60 seconds.</span>
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { num: 1, color: '#FFD600', textColor: 'text-black', title: 'Name Your Org', desc: 'Pick a name and claim your URL (e.g. orgsets.com/springfield-runners). Your group\'s permanent home on the web.' },
                { num: 2, color: '#0044FF', textColor: 'text-white', title: 'Invite Your Team', desc: 'Add officers, assign roles, and set permissions. Share an invite link with members — they join in one click.' },
                { num: 3, color: '#00C389', textColor: 'text-black', title: 'Start Managing', desc: 'Send announcements, collect dues, store documents, and run your organization from a single dashboard.' }
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className="relative p-8 border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] bg-white dark:bg-black"
                >
                  <div className={`absolute -top-6 -left-6 w-12 h-12 border-[4px] border-black dark:border-white ${step.textColor} font-black flex items-center justify-center text-2xl shadow-[4px_4px_0px_#000]`} style={{ backgroundColor: step.color }}>{step.num}</div>
                  <h3 className="text-2xl font-black uppercase mb-4 mt-2">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 font-bold leading-relaxed">{step.desc}</p>
                </motion.div>
              ))}
            </div>
            
            {/* CSV & Free Migration Reassurance */}
            <div className="mt-12 max-w-3xl mx-auto p-4 sm:p-5 bg-white dark:bg-black border-[3px] border-black dark:border-white shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col sm:flex-row items-center justify-between gap-4 text-left">
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 bg-[#0044FF] text-white flex items-center justify-center shrink-0 border-2 border-black dark:border-white shadow-[2px_2px_0px_#000]">
                  <FileSpreadsheet size={22} />
                </div>
                <div>
                  <span className="font-mono text-xs font-black uppercase text-[#0044FF] dark:text-[#FFD600] tracking-wider block">
                    Zero Migration Friction • Bulk Roster Import
                  </span>
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mt-0.5 leading-relaxed">
                    Already have an active roster? Bulk-import members via CSV in 60 seconds. For multi-chapter networks and leagues migrating from Wild Apricot or MemberPlanet, our team will migrate your chapters for free.
                  </p>
                </div>
              </div>
              <Link 
                to="/contact?topic=migration"
                className="font-mono text-xs font-black uppercase tracking-wider text-black dark:text-white underline hover:text-[#0044FF] shrink-0"
              >
                Request Free Concierge Migration →
              </Link>
            </div>

            <div className="mt-12 text-center">
              <Link 
                to="/sign-up"
                onClick={(e) => {
                  if (isSignedIn) {
                    e.preventDefault();
                    setIsWizardOpen(true);
                  }
                }}
                className="inline-flex items-center justify-center bg-black text-white dark:bg-white dark:text-black font-bold uppercase tracking-wider py-4 px-12 rounded-none border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] hover:-translate-y-1 hover:shadow-[12px_12px_0px_#0044FF] transition-all text-xl"
              >
                CREATE YOUR FREE ORG →
              </Link>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 4: CORE CAPABILITIES — 3 cards, humanized copy
        ═══════════════════════════════════════════════════════════════ */}
        <section id="features" className={cn("py-24 px-4 relative overflow-hidden", s.featuresSection)}>
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="mb-16">
              <Badge variant="blue" className="mb-6 border-none px-6 py-2 rounded-none border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] bg-brand-primary text-white font-black cursor-pointer uppercase tracking-widest">
                What You Get
              </Badge>
              <h2 className={cn("text-4xl md:text-6xl font-black font-display tracking-tight leading-[0.9]", s.featuresH2)}>
                All-in-one chapter &<br />
                <span className="text-[#0044FF] dark:text-[#FFD600]">membership management software.</span>
              </h2>
              <p className="mt-6 text-lg md:text-xl font-bold max-w-2xl opacity-70">
                Replace five disconnected tools with one command center built for multi-chapter networks, leagues, and non-profits.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
              {/* Capability 1: Team & Committee Chats */}
              <Link to="/features#chat" className={cn("col-span-1 md:col-span-1 lg:col-span-3 p-8 flex flex-col justify-between group overflow-hidden relative cursor-pointer block", s.bentoCard)}>
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <MessageSquare size={120} />
                </div>
                <div className="relative z-10 mb-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-950/40 border-2 border-black dark:border-white text-xs font-mono font-bold mb-6 flex-wrap">
                    <span className="line-through opacity-60 text-red-600 dark:text-red-400 font-black">GroupMe / Slack</span>
                    <span className="text-black dark:text-white font-bold">→</span>
                    <span className="text-black dark:text-white uppercase tracking-wider font-black">Team Chats</span>
                  </div>
                  <div className="w-16 h-16 bg-indigo-600 mb-6 flex items-center justify-center border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] text-white">
                    <MessageSquare size={32} />
                  </div>
                  <h3 className={cn("text-2xl font-black mb-4 group-hover:text-brand-primary transition-colors", s.bentoTitle)}>Team & Committee Chats</h3>
                  <p className={cn("text-base leading-relaxed", s.bentoText)}>No more rogue group texts or lost direct messages. Structured, role-based channels with threaded discussions that stay inside your organization.</p>
                </div>
              </Link>

              {/* Capability 2: Pulse Announcements */}
              <Link to="/features#pulse" className={cn("col-span-1 md:col-span-1 lg:col-span-3 p-8 flex flex-col justify-between group overflow-hidden relative cursor-pointer block", s.bentoCard)}>
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Zap size={120} />
                </div>
                <div className="relative z-10 mb-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-950/40 border-2 border-black dark:border-white text-xs font-mono font-bold mb-6 flex-wrap">
                    <span className="line-through opacity-60 text-red-600 dark:text-red-400 font-black">Mailchimp + Text Blasts</span>
                    <span className="text-black dark:text-white font-bold">→</span>
                    <span className="text-black dark:text-white uppercase tracking-wider font-black">Pulse Announcements</span>
                  </div>
                  <div className="w-16 h-16 bg-blue-600 mb-6 flex items-center justify-center border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] text-white">
                    <Zap size={32} />
                  </div>
                  <h3 className={cn("text-2xl font-black mb-4 group-hover:text-brand-primary transition-colors", s.bentoTitle)}>Pulse Announcements & Newsletters</h3>
                  <p className={cn("text-base leading-relaxed", s.bentoText)}>Send urgent alerts, schedule changes, and newsletters via SMS, email, and in-app in one click. 100% carrier delivery with zero spam folder worries.</p>
                </div>
              </Link>

              {/* Capability 3: Automatic Dues & Splits */}
              <Link to="/features#ledger" className={cn("col-span-1 md:col-span-1 lg:col-span-2 p-8 flex flex-col justify-between group overflow-hidden relative cursor-pointer block", s.bentoCard)}>
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <CreditCard size={120} />
                </div>
                <div className="relative z-10 mb-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-950/40 border-2 border-black dark:border-white text-xs font-mono font-bold mb-6 flex-wrap">
                    <span className="line-through opacity-60 text-red-600 dark:text-red-400 font-black">Venmo / QuickBooks</span>
                    <span className="text-black dark:text-white font-bold">→</span>
                    <span className="text-black dark:text-white uppercase tracking-wider font-black">Split Ledger</span>
                  </div>
                  <div className="w-16 h-16 bg-emerald-600 mb-6 flex items-center justify-center border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] text-white">
                    <CreditCard size={32} />
                  </div>
                  <h3 className={cn("text-2xl font-black mb-4 group-hover:text-brand-primary transition-colors", s.bentoTitle)}>Automatic Dues & Chapter Splits</h3>
                  <p className={cn("text-base leading-relaxed", s.bentoText)}>The dedicated chapter dues collection platform. Stop chasing Venmo payments. Members pay online via Stripe, and our split engine executes automated national to local dues splitting directly between national HQ and chapter bank accounts.</p>
                  <div className="mt-4 pt-3 border-t border-black/10 dark:border-white/10 flex flex-col gap-1">
                    <span className="text-[11px] font-mono font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                      Transparent Business Model
                    </span>
                    <span className="text-[11px] font-bold opacity-80 leading-snug">
                      Free to start for local clubs (up to 50 members). We only earn a flat $0.50 platform fee per dues payment collected via Stripe. Transparent pricing, zero ads, and zero member data selling.
                    </span>
                  </div>
                </div>
              </Link>

              {/* Capability 4: Role-Based Document Vault */}
              <Link to="/features#vault" className={cn("col-span-1 md:col-span-1 lg:col-span-2 p-8 flex flex-col justify-between group overflow-hidden relative cursor-pointer block", s.bentoCard)}>
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Lock size={120} />
                </div>
                <div className="relative z-10 mb-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-950/40 border-2 border-black dark:border-white text-xs font-mono font-bold mb-6 flex-wrap">
                    <span className="line-through opacity-60 text-red-600 dark:text-red-400 font-black">Google Drive / Dropbox</span>
                    <span className="text-black dark:text-white font-bold">→</span>
                    <span className="text-black dark:text-white uppercase tracking-wider font-black">Role-Based Vault</span>
                  </div>
                  <div className="w-16 h-16 bg-rose-600 mb-6 flex items-center justify-center border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] text-white">
                    <Lock size={32} />
                  </div>
                  <h3 className={cn("text-2xl font-black mb-4 group-hover:text-brand-primary transition-colors", s.bentoTitle)}>Role-Based Document Vault</h3>
                  <p className={cn("text-base leading-relaxed", s.bentoText)}>Bylaws, waivers, insurance certs, and filings stored in a secure vault with role-based access. Automatically shared down to every local chapter.</p>
                </div>
              </Link>

              {/* Capability 5: Live Member & Officer Directory */}
              <Link to="/features#directory" className={cn("col-span-1 md:col-span-2 lg:col-span-2 p-8 flex flex-col justify-between group overflow-hidden relative cursor-pointer block", s.bentoCard)}>
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Users size={120} />
                </div>
                <div className="relative z-10 mb-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-950/40 border-2 border-black dark:border-white text-xs font-mono font-bold mb-6 flex-wrap">
                    <span className="line-through opacity-60 text-red-600 dark:text-red-400 font-black">Messy Google Sheets</span>
                    <span className="text-black dark:text-white font-bold">→</span>
                    <span className="text-black dark:text-white uppercase tracking-wider font-black">Live Directory</span>
                  </div>
                  <div className="w-16 h-16 bg-amber-500 mb-6 flex items-center justify-center border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] text-white">
                    <Users size={32} />
                  </div>
                  <h3 className={cn("text-2xl font-black mb-4 group-hover:text-brand-primary transition-colors", s.bentoTitle)}>Live Member & Officer Directory</h3>
                  <p className={cn("text-base leading-relaxed", s.bentoText)}>Centralize member rosters, officer transitions, parent links, and background checks. Live profiles update in real time with zero manual spreadsheet entry.</p>
                </div>
              </Link>
            </div>

            <div className="mt-12 text-center">
              <Link 
                to="/features"
                className="inline-flex items-center justify-center gap-2 backdrop-blur-sm group rounded-none border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] bg-white dark:bg-black text-black dark:text-white hover:bg-brand-primary hover:text-white dark:hover:text-white transition-all h-14 px-10 font-bold uppercase tracking-wider text-sm font-mono"
              >
                Explore All Chapter Management Features
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION: FREE OPERATIONAL TOOLS — High-Intent Zero-Login Suite
        ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4 bg-white dark:bg-black border-t-[8px] border-black dark:border-white text-black dark:text-white relative overflow-hidden">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="mb-16">
              <Badge variant="blue" className="mb-6 border-none px-6 py-2 rounded-none border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] bg-[#0044FF] text-white font-black uppercase tracking-widest">
                Zero-Login Free Utilities
              </Badge>
              <h2 className={cn("text-4xl md:text-6xl font-black font-display tracking-tight leading-[0.9]", s.featuresH2)}>
                Free chapter calculators & tools<br />
                <span className="text-[#0044FF] dark:text-[#FFD600]">before you even sign up.</span>
              </h2>
              <p className="mt-6 text-lg md:text-xl font-bold max-w-2xl opacity-70">
                100% free operational tools with no login to calculate, print, or export. Save a draft or create a free workspace when you are ready.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Tool 1: Dues Split Calculator */}
              <Link 
                to="/tools/dues-split-calculator"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-6 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#0044FF] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-emerald-600 text-white flex items-center justify-center border-[3px] border-black dark:border-white shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#fff]">
                      <Calculator size={24} />
                    </div>
                    <span className="px-2 py-0.5 bg-[#FFD600] text-black text-[10px] font-black tracking-wider border border-black uppercase">
                      Popular
                    </span>
                  </div>
                  <h3 className="text-xl font-black uppercase tracking-tight mb-2 group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors">
                    Dues Split Calculator
                  </h3>
                  <p className="text-xs font-bold opacity-75 leading-relaxed mb-6">
                    Calculate instant revenue distributions and transaction fee routing between national HQ, regional councils, and local chapters—then activate automated Stripe dues payouts in your free workspace.
                  </p>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                  <span>Launch Tool</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Tool 2: Bylaws & Constitution Builder */}
              <Link 
                to="/tools/bylaws-templates-builder"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-6 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#0044FF] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-purple-600 text-white flex items-center justify-center border-[3px] border-black dark:border-white shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#fff]">
                      <FileText size={24} />
                    </div>
                    <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/60 text-purple-700 dark:text-purple-300 text-[10px] font-black tracking-wider border border-black uppercase">
                      Governance
                    </span>
                  </div>
                  <h3 className="text-xl font-black uppercase tracking-tight mb-2 group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors">
                    Bylaws Builder
                  </h3>
                  <p className="text-xs font-bold opacity-75 leading-relaxed mb-6">
                    Generate starter constitutions, local chapter bylaws, and code of conduct boilerplates in 60s—and save them directly into your chapter's permanent, role-based document vault.
                  </p>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-purple-600 dark:text-purple-400">
                  <span>Launch Tool</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Tool 3: Event Budget Estimator */}
              <Link 
                to="/tools/event-budget-estimator"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-6 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#0044FF] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-600 text-white flex items-center justify-center border-[3px] border-black dark:border-white shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#fff]">
                      <DollarSign size={24} />
                    </div>
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 text-[10px] font-black tracking-wider border border-black uppercase">
                      Finance
                    </span>
                  </div>
                  <h3 className="text-xl font-black uppercase tracking-tight mb-2 group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors">
                    Event Budget & Breakeven
                  </h3>
                  <p className="text-xs font-bold opacity-75 leading-relaxed mb-6">
                    Compute ticket breakeven points, per-attendee catering costs, and safety margins—then sync your event budget straight into your group's financial ledger.
                  </p>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  <span>Launch Tool</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Tool 4: Quorum & Roll Checker */}
              <Link 
                to="/tools/quorum-and-roll-checker"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-6 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#0044FF] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-amber-500 text-black flex items-center justify-center border-[3px] border-black dark:border-white shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#fff]">
                      <CheckSquare size={24} />
                    </div>
                    <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 text-[10px] font-black tracking-wider border border-black uppercase">
                      Robert's Rules
                    </span>
                  </div>
                  <h3 className="text-xl font-black uppercase tracking-tight mb-2 group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors">
                    Quorum & Roll Checker
                  </h3>
                  <p className="text-xs font-bold opacity-75 leading-relaxed mb-6">
                    Quickly calculate legal quorum thresholds and voter majorities under Robert's Rules—then publish verified roll-call records to your organization directory.
                  </p>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-amber-600 dark:text-amber-400">
                  <span>Launch Tool</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>

            {/* 1-Click Workspace Sync Handoff Banner */}
            <div className="mt-10 p-5 bg-blue-50 dark:bg-blue-950/40 border-[3px] border-black dark:border-white shadow-[6px_6px_0px_#0044FF] dark:shadow-[6px_6px_0px_#FFD600] flex flex-col md:flex-row items-center justify-between gap-5 text-left">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-[#0044FF] text-white flex items-center justify-center shrink-0 border-2 border-black dark:border-white shadow-[3px_3px_0px_#000]">
                  <Zap size={22} />
                </div>
                <div>
                  <div className="text-xs font-mono font-black uppercase tracking-wider text-[#0044FF] dark:text-[#FFD600]">
                    💡 1-Click Workspace Sync
                  </div>
                  <div className="text-sm font-bold text-gray-800 dark:text-gray-200 mt-0.5 leading-relaxed">
                    Every calculation, budget draft, and bylaws document you generate in our free utilities can be saved straight into a permanent free OrgSets workspace in one click.
                  </div>
                </div>
              </div>
              <button 
                type="button"
                onClick={handleClaimSubmit}
                className="px-6 py-3 bg-black text-white dark:bg-white dark:text-black font-mono text-xs font-black uppercase tracking-wider shrink-0 border-2 border-black dark:border-white hover:bg-[#0044FF] hover:text-white transition-colors shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#fff]"
              >
                Claim Free Workspace →
              </button>
            </div>

            <div className="mt-12 text-center">
              <Link 
                to="/tools"
                className="inline-flex items-center justify-center gap-3 bg-white dark:bg-black border-[4px] border-black dark:border-white text-black dark:text-white hover:bg-brand-primary hover:text-white transition-colors rounded-none py-4 px-10 h-auto text-lg font-bold uppercase tracking-widest font-mono shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff]"
              >
                Browse All 100+ Free Organization Tools & Calculators
                <ArrowRight size={18} className="ml-3" />
              </Link>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 5: USE CASES — Built for how your organization works
        ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4 bg-gray-50 dark:bg-[#0A0A0A] border-t-[8px] border-black dark:border-white text-black dark:text-white relative overflow-hidden">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="mb-16">
              <Badge variant="amber" className="mb-6 border-none px-6 py-2 rounded-none border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] bg-[#FFD600] text-black font-black uppercase tracking-widest">
                Who We Serve
              </Badge>
              <h2 className={cn("text-4xl md:text-6xl font-black font-display tracking-tight leading-[0.9]", s.featuresH2)}>
                Built for how your<br />
                <span className="text-gray-400">organization actually works.</span>
              </h2>
              <p className="mt-6 text-lg md:text-xl font-bold max-w-2xl opacity-70">
                Whether you run a national chapter network, a local youth sports league, or a non-profit board, OrgSets gives you structured operations without the administrative headaches.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Persona 1: Chapter Networks */}
              <div className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-8 shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] flex flex-col justify-between">
                <div>
                  <div className="w-14 h-14 bg-[#0044FF] text-white flex items-center justify-center border-[3px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] mb-6">
                    <GraduationCap size={28} />
                  </div>
                  <div className="text-xs font-mono font-black uppercase tracking-widest text-[#0044FF] mb-2">
                    Fraternities • Alumni • Civic Orgs
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-4">
                    National & Regional Chapters
                  </h3>
                  <p className="font-bold text-sm opacity-80 mb-6 leading-relaxed">
                    HQ needs visibility into chapter bank accounts and rosters, while local officers need independence without losing historical records.
                  </p>
                  <ul className="space-y-3 font-bold text-sm mb-8 border-t-2 border-black/10 dark:border-white/10 pt-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-[#0044FF] shrink-0 mt-0.5" />
                      <span><strong>Split-Ledger Dues:</strong> Automated national to local dues splitting routes percentage cuts from local chapters straight to national HQ.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-[#0044FF] shrink-0 mt-0.5" />
                      <span><strong>Cascading Governance:</strong> Push bylaws, safety rules, and requirements down the org tree.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-[#0044FF] shrink-0 mt-0.5" />
                      <span><strong>Officer Succession:</strong> Permissions stay with the role, so historical files never get lost.</span>
                    </li>
                  </ul>
                </div>
                <Link to="/use-cases#alumni" className="inline-flex items-center gap-2 font-black text-sm uppercase tracking-wider text-[#0044FF] hover:underline">
                  Chapter Network Playbook <ArrowRight size={16} />
                </Link>
              </div>

              {/* Persona 2: Sports Leagues & Rec Clubs */}
              <div className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-8 shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] flex flex-col justify-between">
                <div>
                  <div className="w-14 h-14 bg-emerald-600 text-white flex items-center justify-center border-[3px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] mb-6">
                    <Trophy size={28} />
                  </div>
                  <div className="text-xs font-mono font-black uppercase tracking-widest text-emerald-600 mb-2">
                    Youth Sports • Rec Leagues • Run Clubs
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-4">
                    Sports Leagues & Rec Clubs
                  </h3>
                  <p className="font-bold text-sm opacity-80 mb-6 leading-relaxed">
                    The complete club & association management software for youth sports, recreational leagues, and run clubs. Stop letting rainouts get buried in group chats and chasing parents for signed liability waivers.
                  </p>
                  <ul className="space-y-3 font-bold text-sm mb-8 border-t-2 border-black/10 dark:border-white/10 pt-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>1-Click Rainout Alerts:</strong> Instant emergency SMS broadcasts sent to all parents and players.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Digital Waiver Vault:</strong> Collect and store signed liability waivers upon member registration.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Season Dues Collection:</strong> Credit card and ACH dues collection without chasing Venmo.</span>
                    </li>
                  </ul>
                </div>
                <Link to="/use-cases#sports" className="inline-flex items-center gap-2 font-black text-sm uppercase tracking-wider text-emerald-600 hover:underline">
                  Sports League Playbook <ArrowRight size={16} />
                </Link>
              </div>

              {/* Persona 3: HOAs & Local Boards */}
              <div className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-8 shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] flex flex-col justify-between">
                <div>
                  <div className="w-14 h-14 bg-amber-500 text-black flex items-center justify-center border-[3px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] mb-6">
                    <Building2 size={28} />
                  </div>
                  <div className="text-xs font-mono font-black uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-2">
                    HOAs • Non-Profit Boards • Foundations
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-4">
                    HOA & Nonprofit Board Portal
                  </h3>
                  <p className="font-bold text-sm opacity-80 mb-6 leading-relaxed">
                    The dedicated HOA & nonprofit board portal. New board members shouldn't inherit a shoebox of papers, personal Google Drives, or lost passwords when taking office.
                  </p>
                  <ul className="space-y-3 font-bold text-sm mb-8 border-t-2 border-black/10 dark:border-white/10 pt-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-amber-500 shrink-0 mt-0.5" />
                      <span><strong>Audit-Ready Vault:</strong> Encrypted storage for tax filings, meeting minutes, and legal bylaws.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-amber-500 shrink-0 mt-0.5" />
                      <span><strong>Clean Officer Handoffs:</strong> Access tied to the title (Treasurer, Secretary) rather than personal accounts.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 size={16} className="text-amber-500 shrink-0 mt-0.5" />
                      <span><strong>Verified Directory:</strong> Private resident and donor directory with granular privacy settings.</span>
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t-2 border-black/10 dark:border-white/10">
                  <Link to="/use-cases#nonprofit" className="inline-flex items-center gap-2 font-black text-sm uppercase tracking-wider text-amber-600 dark:text-amber-400 hover:underline">
                    Board & HOA Playbook <ArrowRight size={16} />
                  </Link>
                  <Link to="/board-memo" className="inline-flex items-center gap-1 font-mono text-xs font-bold text-gray-500 hover:text-black dark:hover:text-white underline">
                    Need board approval? View 1-page memo →
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-16 text-center">
              <Link 
                to="/use-cases"
                className="inline-flex items-center justify-center gap-3 bg-white dark:bg-black border-[4px] border-black dark:border-white text-black dark:text-white hover:bg-brand-primary hover:text-white transition-colors rounded-none py-4 px-10 h-auto text-lg font-bold uppercase tracking-widest font-mono shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff]"
              >
                Explore All 13+ Organization Playbooks & Use Cases
                <ArrowRight size={18} className="ml-3" />
              </Link>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION: COMPETITOR COMPARISON — The Legacy Giants Matrix
        ═══════════════════════════════════════════════════════════════ */}
        <section id="compare" className="py-24 px-4 bg-gray-100 dark:bg-[#070707] border-t-[8px] border-black dark:border-white text-black dark:text-white relative overflow-hidden">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="mb-16">
              <Badge variant="amber" className="mb-6 border-none px-6 py-2 rounded-none border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] bg-[#FFD600] text-black font-black uppercase tracking-widest">
                Head-to-Head Comparison
              </Badge>
              <h2 className={cn("text-4xl md:text-6xl font-black font-display tracking-tight leading-[0.9]", s.featuresH2)}>
                Why boards hate paying for<br />
                <span className="text-[#0044FF] dark:text-[#FFD600]">legacy software giants.</span>
              </h2>
              <p className="mt-6 text-lg md:text-xl font-bold max-w-2xl opacity-70">
                Volunteer boards are tired of paying thousands for software built twenty years ago. Compare OrgSets as the modern Wild Apricot alternative for chapters, clubs, and leagues actively migrating away from legacy tools.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Competitor 1: WildApricot */}
              <Link 
                to="/compare/wild-apricot"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-7 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#f97316] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-black uppercase tracking-widest text-[#f97316]">
                      OrgSets vs Wild Apricot
                    </span>
                    <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-red-100 dark:bg-red-950/60 text-red-600 border border-red-300 dark:border-red-800 uppercase">
                      Contact Limits
                    </span>
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-3 group-hover:text-[#f97316] transition-colors">
                    Wild Apricot
                  </h3>
                  <div className="space-y-2 mb-6 text-xs font-bold">
                    <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300">
                      <strong>The Legacy Trap:</strong> Punitive contact caps reaching ~$500/mo for 2,000 members. Zero multi-tier chapter hierarchy.
                    </div>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      <strong>The OrgSets Fix:</strong> Flat transparent pricing ($0–$199/mo), infinite chapter hierarchy, and automated Stripe dues splitting.
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-[#f97316]">
                  <span>View Wild Apricot Matrix</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Competitor 2: MemberPlanet */}
              <Link 
                to="/compare/memberplanet"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-7 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#2563eb] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-black uppercase tracking-widest text-[#2563eb]">
                      OrgSets vs MemberPlanet
                    </span>
                    <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-red-100 dark:bg-red-950/60 text-red-600 border border-red-300 dark:border-red-800 uppercase">
                      2–4% Cuts
                    </span>
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-3 group-hover:text-[#2563eb] transition-colors">
                    MemberPlanet
                  </h3>
                  <div className="space-y-2 mb-6 text-xs font-bold">
                    <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300">
                      <strong>The Legacy Trap:</strong> Surcharges 2–4% platform commission on member dues. Clunky legacy mobile web forms.
                    </div>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      <strong>The OrgSets Fix:</strong> 0% platform commission (flat $0.50/tx), modern mobile UX, and role-based document vault.
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-[#2563eb]">
                  <span>View MemberPlanet Matrix</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Competitor 3: ClubExpress */}
              <Link 
                to="/compare/clubexpress"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-7 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#0284c7] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-black uppercase tracking-widest text-[#0284c7]">
                      OrgSets vs ClubExpress
                    </span>
                    <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-red-100 dark:bg-red-950/60 text-red-600 border border-red-300 dark:border-red-800 uppercase">
                      $500 Setup
                    </span>
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-3 group-hover:text-[#0284c7] transition-colors">
                    ClubExpress
                  </h3>
                  <div className="space-y-2 mb-6 text-xs font-bold">
                    <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300">
                      <strong>The Legacy Trap:</strong> Mandatory $150–$500+ onboarding fee, 2005-era desktop layout, and escalating per-member monthly taxes.
                    </div>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      <strong>The OrgSets Fix:</strong> $0 setup fee, 60-second self-serve launch, modern mobile interface, and free tier for up to 50 members.
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-[#0284c7]">
                  <span>View ClubExpress Matrix</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Competitor 4: Zeffy */}
              <Link 
                to="/compare/zeffy"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-7 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#10b981] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-black uppercase tracking-widest text-[#10b981]">
                      OrgSets vs Zeffy
                    </span>
                    <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-amber-100 dark:bg-amber-950/60 text-amber-700 border border-amber-300 dark:border-amber-800 uppercase">
                      Tip Jar
                    </span>
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-3 group-hover:text-[#10b981] transition-colors">
                    Zeffy
                  </h3>
                  <div className="space-y-2 mb-6 text-xs font-bold">
                    <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 text-amber-800 dark:text-amber-300">
                      <strong>The Legacy Trap:</strong> Checkout aggressively prompts donors for 15–20% tips. No member directories, document vaults, or SMS.
                    </div>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      <strong>The OrgSets Fix:</strong> Clean flat-fee Stripe processing with zero tip prompts, full governance suite, and emergency pulse alerts.
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-[#10b981]">
                  <span>View Zeffy Matrix</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Competitor 5: GroupMe */}
              <Link 
                to="/compare/groupme"
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white p-7 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#00aff0] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-black uppercase tracking-widest text-[#00aff0]">
                      OrgSets vs GroupMe
                    </span>
                    <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-red-100 dark:bg-red-950/60 text-red-600 border border-red-300 dark:border-red-800 uppercase">
                      Chat Chaos
                    </span>
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-3 group-hover:text-[#00aff0] transition-colors">
                    GroupMe
                  </h3>
                  <div className="space-y-2 mb-6 text-xs font-bold">
                    <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300">
                      <strong>The Legacy Trap:</strong> Critical alerts get lost in conversational chatter. Zero dues collection, no waivers, and no verified rosters.
                    </div>
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                      <strong>The OrgSets Fix:</strong> Structured rosters, 1-click emergency SMS broadcasts, automatic Stripe dues collection, and legal waiver vault.
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-xs font-black uppercase tracking-wider text-[#00aff0]">
                  <span>View GroupMe Matrix</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* Hub Card: View All Comparisons */}
              <Link 
                to="/compare"
                className="bg-[#0044FF] text-white border-[4px] border-black dark:border-white p-7 shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#ffffff] flex flex-col justify-between hover:-translate-y-1 hover:shadow-[10px_10px_0px_#000] dark:hover:shadow-[10px_10px_0px_#fff] transition-all group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-black uppercase tracking-widest text-[#FFD600]">
                      Full Platform Directory
                    </span>
                  </div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-3">
                    Compare All 8+ Platforms
                  </h3>
                  <p className="text-xs font-bold opacity-90 leading-relaxed mb-6">
                    See exhaustive feature matrices and fee calculations comparing OrgSets with Hivebrite, Glue Up, NationBuilder, Wild Apricot, MemberPlanet, ClubExpress, Zeffy, and GroupMe.
                  </p>
                </div>
                <div className="pt-4 border-t-2 border-white/20 flex items-center justify-between text-xs font-black uppercase tracking-wider text-[#FFD600]">
                  <span>Explore All Matrices</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </div>

            {/* Competitor Migration Guarantee Banner */}
            <div className="mt-12 p-6 bg-[#FFD600] text-black border-[4px] border-black shadow-[8px_8px_0px_#000000] flex flex-col md:flex-row items-center justify-between gap-6 text-left">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-black text-[#FFD600] flex items-center justify-center shrink-0 border-2 border-black shadow-[3px_3px_0px_rgba(0,0,0,0.3)]">
                  <FileSpreadsheet size={26} />
                </div>
                <div>
                  <div className="font-mono text-xs font-black uppercase tracking-wider">
                    Stuck on Wild Apricot, MemberPlanet, or Spreadsheets?
                  </div>
                  <div className="text-sm md:text-base font-black mt-0.5 leading-snug">
                    Upload your CSV roster in 60 seconds, or let our engineering team migrate your chapters, dues history, and files completely for free.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 shrink-0 flex-wrap">
                <button 
                  type="button"
                  onClick={handleClaimSubmit}
                  className="bg-black text-white hover:bg-neutral-800 font-mono text-xs font-black uppercase tracking-wider rounded-none border-2 border-black px-5 py-3 shadow-[3px_3px_0px_rgba(0,0,0,0.3)]"
                >
                  Start Free Switch →
                </button>
                <Link 
                  to="/contact?topic=migration"
                  className="font-mono text-xs font-black uppercase tracking-wider underline hover:opacity-80"
                >
                  Free Concierge Migration →
                </Link>
              </div>
            </div>

            <div className="mt-12 text-center">
              <Link 
                to="/compare"
                className="inline-flex items-center justify-center gap-3 bg-white dark:bg-black border-[4px] border-black dark:border-white text-black dark:text-white hover:bg-brand-primary hover:text-white transition-colors rounded-none py-4 px-10 h-auto text-lg font-bold uppercase tracking-widest font-mono shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff]"
              >
                Compare OrgSets Against All 8+ Community Platforms
                <ArrowRight size={18} className="ml-3" />
              </Link>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
            SECTION 6: FINAL CTA + INLINE FAQ — Merged for tighter close
        ═══════════════════════════════════════════════════════════════ */}
        <section className="py-24 px-4 bg-brand-primary text-white border-t-[8px] border-black dark:border-white">
          <div className="max-w-5xl mx-auto">
            {/* CTA Block */}
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tighter">
                Ready to bring order to your organization?
              </h2>
              <p className="text-xl md:text-2xl mb-10 font-bold opacity-90">
                Launch your free org in under 60 seconds. No credit card required.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <Link
                  to="/sign-up"
                  onClick={(e) => {
                    if (isSignedIn) {
                      e.preventDefault();
                      setIsWizardOpen(true);
                    }
                  }}
                  className={cn(
                    "inline-flex items-center justify-center px-12 py-5 text-xl font-black font-mono uppercase tracking-wider group transition-all duration-300 transform hover:scale-105",
                    s.heroButtonClass
                  )}
                >
                  {copy?.heroCta || 'Create Your Free Organization →'}
                  <Zap className="ml-3 group-hover:scale-110 transition-transform fill-current" size={20} />
                </Link>
              </div>
            </div>

            {/* Inline FAQ */}
            <div className="border-t-[4px] border-white/20 pt-16">
              <h3 className="text-2xl font-black uppercase tracking-tight mb-8 text-center">Common Questions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {LANDING_FAQS.map((faq, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm p-6 border-[2px] border-white/20"
                  >
                    <h4 className="text-lg font-black uppercase tracking-tight mb-3">{faq.q}</h4>
                    <p className="font-bold opacity-80 text-sm leading-relaxed">{faq.a}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>

      <PublicFooter />

      <SetupWizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} initialName={heroOrgName} />
    </div>
  );
};
